import useCustomize from '@/common/hooks/useCustomize';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import useWebView from '@/common/hooks/useWebView';
import classNames from '@/common/utils/classNames';
import { BottomNavigationSlot } from './bottomNavigation/floatingBottomNavigation';
import { ReactNode } from 'react';
import Footer from './footer';
import CompactFooter from './footer/compactFooter';
import Header from './header';
import NewAppHeaderBar, { NewAppHeaderBarProps } from './newAppHeaderBar';

export const LayoutWithHeaderAndFooter = ({
  children,
  shouldShowBrand = true,
  shouldShowPromoteApp = true,
  showBottomNavigation = true,
  showHeader = true,
  showFooter = true,
  compactFooter = false,
  showSearchSuggestionButton = false,
  className,
  fullBleedUnderBottomNav = false,
  appBar,
}: {
  children: ReactNode;
  shouldShowBrand?: boolean;
  shouldShowPromoteApp?: boolean;
  showBottomNavigation?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  showSearchSuggestionButton?: boolean;
  compactFooter?: boolean;
  className?: string;
  /** Embedded (iframe) pages: in the new app the content runs under the floating nav, full height. */
  fullBleedUnderBottomNav?: boolean;
  /** New app: replaces the site header (title/back override), or `false` when the page renders its own AppBar. */
  appBar?: NewAppHeaderBarProps | false;
}) => {
  const customize = useCustomize(state => state.customize);
  const isWebView = useWebView();
  const isNewApp = useIsNewAppShell();

  return (
    <div
      data-nav-full-bleed={fullBleedUnderBottomNav || undefined}
      data-app-layout=""
      className={classNames('min-h-screen w-full flex flex-col', { 'pb-[calc(4rem_+_env(safe-area-inset-bottom))] md:pb-0': showBottomNavigation }, className)}>
      {customize.showHeader &&
        !isWebView &&
        showHeader &&
        (isNewApp ? (
          appBar !== false && <NewAppHeaderBar {...appBar} />
        ) : (
          // data-site-header: hidden by CSS before hydration in the new app (globals.css).
          <div data-site-header className="contents">
            <Header
              showSearchSuggestionButton={showSearchSuggestionButton}
              shouldShowBrand={customize.showBrandLogoInHomePage || shouldShowBrand}
              shouldShowPromoteApp={shouldShowPromoteApp}
            />
          </div>
        ))}
      <div className="flex min-h-0 w-full flex-grow flex-col">{children}</div>
      {customize.showFooter &&
        !isWebView &&
        showFooter &&
        (customize.footerType === 'compact' || compactFooter ? <CompactFooter /> : <Footer />)}
      {showBottomNavigation && !isWebView && <BottomNavigationSlot />}
    </div>
  );
};
